import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

const DashboardIntegrityAnimation = () => {
  const controls = useAnimationControls();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const runAnimation = async () => {
      try {
        await controls.start("reset");
        await controls.start("animate");
        await new Promise(resolve => setTimeout(resolve, 5000));
        if (isMounted) {
          runAnimation();
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };
    
    runAnimation();
    
    return () => {
      controls.stop();
    };
  }, [isMounted, controls]);

  return (
    <div className="relative w-full overflow-hidden">
      <motion.div 
        style={{height: '100%'}}
        className="bg-[#EDE9FE] rounded-xl p-3 sm:p-4 relative h-[400px] sm:h-[350px] flex items-center justify-center transform-gpu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Main Dashboard Container */}
        <motion.div 
          className="bg-white rounded-lg shadow-lg w-full max-w-4xl overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={controls}
          variants={{
            reset: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0, transition: { delay: 0.5 } }
          }}
        >
          {/* Dashboard Header */}
          <div className="bg-indigo-600 p-2 flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex space-x-2">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
              </div>
              <span className="text-white text-xs ml-3">Qupid Dashboard Integrity Monitor</span>
            </div>
            <div className="text-white text-xs bg-indigo-700 px-2 py-0.5 rounded">
              Real-time monitoring
            </div>
          </div>

          {/* Dashboard Content */}
          <div className="p-2 sm:p-3 space-y-2">
            {/* Dashboard Health Overview */}
            <motion.div
              className="bg-gray-50 rounded-lg p-2"
              initial={{ opacity: 0 }}
              animate={controls}
              variants={{
                reset: { opacity: 0 },
                animate: { opacity: 1, transition: { delay: 1 } }
              }}
            >
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-xs font-medium text-gray-700">Dashboard Health Overview</h3>
                <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                  Updated 2m ago
                </span>
              </div>
              
              <div className="grid grid-cols-4 gap-1 mb-1">
                {[
                  { label: "Healthy", value: "42", color: "bg-green-100 text-green-800", icon: "✓" },
                  { label: "Warning", value: "7", color: "bg-yellow-100 text-yellow-800", icon: "!" },
                  { label: "Critical", value: "2", color: "bg-red-100 text-red-800", icon: "×" },
                  { label: "Total", value: "51", color: "bg-blue-100 text-blue-800", icon: "Σ" }
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    className={`${stat.color} rounded-md p-1.5 text-center`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={controls}
                    variants={{
                      reset: { opacity: 0, scale: 0.9 },
                      animate: { 
                        opacity: 1, 
                        scale: 1, 
                        transition: { delay: 1.2 + (i * 0.1) } 
                      }
                    }}
                  >
                    <div className="flex justify-center items-center space-x-1">
                      <span className="text-xs">{stat.icon}</span>
                      <div className="text-base font-bold">{stat.value}</div>
                    </div>
                    <div className="text-xs">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Dashboard Lineage Visualization */}
            <motion.div
              className="bg-gray-50 rounded-lg p-2"
              initial={{ opacity: 0 }}
              animate={controls}
              variants={{
                reset: { opacity: 0 },
                animate: { opacity: 1, transition: { delay: 1.8 } }
              }}
            >
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-xs font-medium text-gray-700">End-to-End Dashboard Lineage</h3>
                <span className="text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">
                  Impact Analysis
                </span>
              </div>
              
              <div className="relative h-28 mb-1" style={{marginTop: '-40px'}}>
                {/* Connection Lines */}
                <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
                  {/* Source to Transformation */}
                  <motion.path
                    d="M40,40 C80,40 80,70 120,70"
                    stroke="#6366F1"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={controls}
                    variants={{
                      reset: { pathLength: 0 },
                      animate: { pathLength: 1, transition: { delay: 2.0, duration: 0.5 } }
                    }}
                  />
                  
                  {/* Source to Transformation 2 */}
                  <motion.path
                    d="M40,40 C80,40 80,110 120,110"
                    stroke="#6366F1"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={controls}
                    variants={{
                      reset: { pathLength: 0 },
                      animate: { pathLength: 1, transition: { delay: 2.1, duration: 0.5 } }
                    }}
                  />
                  
                  {/* Transformation to Warehouse */}
                  <motion.path
                    d="M120,70 C160,70 160,70 200,70"
                    stroke="#10B981"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={controls}
                    variants={{
                      reset: { pathLength: 0 },
                      animate: { pathLength: 1, transition: { delay: 2.2, duration: 0.5 } }
                    }}
                  />
                  
                  {/* Transformation 2 to Data Lake */}
                  <motion.path
                    d="M120,110 C160,110 160,110 200,110"
                    stroke="#10B981"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={controls}
                    variants={{
                      reset: { pathLength: 0 },
                      animate: { pathLength: 1, transition: { delay: 2.3, duration: 0.5 } }
                    }}
                  />
                  
                  {/* Warehouse to Dashboard 1 */}
                  <motion.path
                    d="M200,70 C240,70 240,60 280,60"
                    stroke="#10B981"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={controls}
                    variants={{
                      reset: { pathLength: 0 },
                      animate: { pathLength: 1, transition: { delay: 2.4, duration: 0.5 } }
                    }}
                  />
                  
                  {/* Data Lake to Dashboard 2 */}
                  <motion.path
                    d="M200,110 C240,110 240,90 280,90"
                    stroke="#10B981"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={controls}
                    variants={{
                      reset: { pathLength: 0 },
                      animate: { pathLength: 1, transition: { delay: 2.5, duration: 0.5 } }
                    }}
                  />
                  
                  {/* Data Flow Animations */}
                  <motion.circle
                    r="3"
                    fill="#6366F1"
                    initial={{ cx: 40, cy: 40 }}
                    animate={controls}
                    variants={{
                      reset: { cx: 40, cy: 40, opacity: 0 },
                      animate: { 
                        cx: [40, 120], 
                        cy: [40, 70], 
                        opacity: [0, 1, 1, 0],
                        transition: { 
                          delay: 2.8, 
                          duration: 0.8,
                          times: [0, 0.1, 0.9, 1]
                        } 
                      }
                    }}
                  />
                  
                  <motion.circle
                    r="3"
                    fill="#6366F1"
                    initial={{ cx: 40, cy: 40 }}
                    animate={controls}
                    variants={{
                      reset: { cx: 40, cy: 40, opacity: 0 },
                      animate: { 
                        cx: [40, 120], 
                        cy: [40, 110], 
                        opacity: [0, 1, 1, 0],
                        transition: { 
                          delay: 3.0, 
                          duration: 0.8,
                          times: [0, 0.1, 0.9, 1]
                        } 
                      }
                    }}
                  />
                  
                  <motion.circle
                    r="3"
                    fill="#10B981"
                    initial={{ cx: 120, cy: 70 }}
                    animate={controls}
                    variants={{
                      reset: { cx: 120, cy: 70, opacity: 0 },
                      animate: { 
                        cx: [120, 200], 
                        cy: [70, 70], 
                        opacity: [0, 1, 1, 0],
                        transition: { 
                          delay: 3.2, 
                          duration: 0.8,
                          times: [0, 0.1, 0.9, 1]
                        } 
                      }
                    }}
                  />
                  
                  <motion.circle
                    r="3"
                    fill="#10B981"
                    initial={{ cx: 120, cy: 110 }}
                    animate={controls}
                    variants={{
                      reset: { cx: 120, cy: 110, opacity: 0 },
                      animate: { 
                        cx: [120, 200], 
                        cy: [110, 110], 
                        opacity: [0, 1, 1, 0],
                        transition: { 
                          delay: 3.3, 
                          duration: 0.8,
                          times: [0, 0.1, 0.9, 1]
                        } 
                      }
                    }}
                  />
                  
                  <motion.circle
                    r="3"
                    fill="#10B981"
                    initial={{ cx: 200, cy: 70 }}
                    animate={controls}
                    variants={{
                      reset: { cx: 200, cy: 70, opacity: 0 },
                      animate: { 
                        cx: [200, 280], 
                        cy: [70, 60], 
                        opacity: [0, 1, 1, 0],
                        transition: { 
                          delay: 3.4, 
                          duration: 0.8,
                          times: [0, 0.1, 0.9, 1]
                        } 
                      }
                    }}
                  />
                  
                  <motion.circle
                    r="3"
                    fill="#10B981"
                    initial={{ cx: 200, cy: 110 }}
                    animate={controls}
                    variants={{
                      reset: { cx: 200, cy: 110, opacity: 0 },
                      animate: { 
                        cx: [200, 280], 
                        cy: [110, 90], 
                        opacity: [0, 1, 1, 0],
                        transition: { 
                          delay: 3.5, 
                          duration: 0.8,
                          times: [0, 0.1, 0.9, 1]
                        } 
                      }
                    }}
                  />
                </svg>
                
                {/* Nodes */}
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "26px", top: "26px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 1.9 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    DB
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Source</span>
                </motion.div>
                
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "106px", top: "56px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 2.0 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    T1
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Transform</span>
                </motion.div>
                
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "106px", top: "96px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 2.1 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    T2
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Transform</span>
                </motion.div>
                
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "186px", top: "56px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 2.2 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    DW
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Warehouse</span>
                </motion.div>
                
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "186px", top: "96px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 2.3 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    DL
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Data Lake</span>
                </motion.div>
                
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "266px", top: "46px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 2.4 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    D1
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Sales</span>
                </motion.div>
                
                <motion.div
                  className="absolute flex flex-col items-center"
                  style={{ left: "266px", top: "76px", transform: "translate(-50%, -50%)", zIndex: 1 }}
                  initial={{ scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scale: 0 },
                    animate: { scale: 1, transition: { delay: 2.5 } }
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                    D2
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">Marketing</span>
                </motion.div>
                
                {/* Alert Animation */}
                <motion.div
                  className="absolute"
                  style={{ left: "276px", top: "36px", zIndex: 2 }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={controls}
                  variants={{
                    reset: { opacity: 0, scale: 0 },
                    animate: { 
                      opacity: [0, 1, 1, 0],
                      scale: [0, 1.2, 1, 0],
                      transition: { 
                        delay: 3.6, 
                        duration: 1.5,
                        times: [0, 0.2, 0.8, 1]
                      } 
                    }
                  }}
                >
                  <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-white text-[10px] font-bold shadow-md">
                    !
                  </div>
                </motion.div>
              </div>
              
              <motion.div
                style={{marginTop: '35px'}}
                className="text-[10px] text-gray-500 text-center"
                initial={{ opacity: 0 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0 },
                  animate: { opacity: 1, transition: { delay: 2.6 } }
                }}
              >
                Visualizing complete data flow from source to dashboards
              </motion.div>
            </motion.div>

            {/* Dashboard Alerts */}
            <motion.div
              className="bg-gray-50 rounded-lg p-2"
              initial={{ opacity: 0 }}
              animate={controls}
              variants={{
                reset: { opacity: 0 },
                animate: { opacity: 1, transition: { delay: 3.8 } }
              }}
            >
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-xs font-medium text-gray-700">Proactive Dashboard Alerts</h3>
                <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full animate-pulse">
                  2 Critical Issues
                </span>
              </div>
              
              <div className="space-y-1.5">
                {[
                  { 
                    title: "Sales Dashboard Refresh Failed", 
                    time: "10m ago",
                    severity: "Critical",
                    color: "bg-red-100 border-red-500",
                    details: "Upstream data quality issue"
                  },
                  { 
                    title: "Marketing Dashboard Data Stale", 
                    time: "25m ago",
                    severity: "Warning",
                    color: "bg-yellow-100 border-yellow-500",
                    details: "Last refresh: 48h ago"
                  },
                  { 
                    title: "Finance Report Query Performance", 
                    time: "1h ago",
                    severity: "Warning",
                    color: "bg-yellow-100 border-yellow-500",
                    details: "Query 2.5x slower"
                  }
                ].map((alert, i) => (
                  <motion.div
                    key={i}
                    className={`${alert.color} border-l-4 rounded-r-md p-1.5 text-xs`}
                    initial={{ x: 50, opacity: 0 }}
                    animate={controls}
                    variants={{
                      reset: { x: 50, opacity: 0 },
                      animate: { 
                        x: 0, 
                        opacity: 1, 
                        transition: { delay: 4.0 + (i * 0.2) } 
                      }
                    }}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium">{alert.title}</span>
                      <span className="text-[10px] text-gray-500">{alert.time}</span>
                    </div>
                    <div className="text-[10px] mt-0.5 flex justify-between">
                      <span>{alert.details}</span>
                      <span className="font-medium">{alert.severity}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Processing Indicator */}
        <motion.div
          className="absolute bottom-2 right-2 flex space-x-1"
          animate={{
            opacity: [0.3, 1, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "linear"
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 bg-indigo-500 rounded-full"
              style={{
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DashboardIntegrityAnimation; 